import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { badgeFileName, badgeState, type BadgeState } from "../badge/badge";
import { downloadBadge } from "../badge/pdf";
import { QrCode } from "../badge/QrCode";
import { useEvent } from "../context/EventProvider";
import { logAction, useAccessLog } from "../log/actionLog";
import { CONFIRMATION, firstStep } from "../funnel/thread";
import { useResume } from "../funnel/useArticles";
import { useCompanions } from "../funnel/useCompanions";
import { useOffer } from "../funnel/useOffre";
import { useParticipation } from "../funnel/useParticipation";
import { useCompany } from "../funnel/useSociete";
import { useSitePages } from "../pages/nav";
import { labelKey, pageOf } from "../pages/registry";
import { useMode } from "../panel/mode";
import { bornes } from "../settings/complete";
import {
  useFlagOr,
  useLocale,
  useSettings,
  useT,
  useText,
} from "../settings/context";
import { Button } from "../ui/Button";
import { Icon } from "../ui/Icon";
import { Prose } from "../ui/Prose";
import { InPlace } from "../panel/InPlace";
import { withContext } from "./registry";
import { fallbackOf } from "../settings/catalog";
import { useEventName } from "../settings/eventName";
import { sendActionEmail } from "../emails/send";
import { useState } from "react";
import { formatFullName } from "../funnel/nomAffiche";

/**
 * ═══ MY BADGE ═══
 *
 * The card you show at the door, and the PDF that goes with it. Ported from the
 * prototype (`data-p="badge"`): same classes, same order, same settings — those are the
 * contract sold to the client, and the free-CSS hatch aims at those selectors.
 *
 * ⚠️ WHAT IT SHOWS IS TRUE, OR IT SHOWS NOTHING. Three things set it apart from a
 * pretty card:
 *
 *   1. THE QR ENCODES `UserEvents.code`, the one the entrance scanner reads — the same
 *      content as the badge the server prints. No code, no QR: a square that scans and
 *      matches nobody costs more time at the door than a missing badge.
 *   2. THE BADGE IS A BUSINESS STATE. It is only issued against a real registration,
 *      and not while a balance is due. The prototype disables that lock so it can be
 *      browsed; here it holds. See `badge/badge.ts`, where the rule is isolated and
 *      tested.
 *   3. COMPANIONS ARE HOLDERS, not lines. Each has their own account, their own
 *      registration and their OWN code: their card carries their own QR, never the
 *      holder's — two people on one registration, and the entrance count only ever sees
 *      one.
 *
 * ⚠️ AN ORGANISER SEES THIS SCREEN LIKE ANY OTHER, and safely: this page only READS. It
 * therefore needs none of the confirmation's "organiser mode" caution, which exists
 * because that screen WRITES a registration.
 *
 * ⚠️ EVERY DISPLAYED STRING GOES THROUGH `t()`, French kept at the call site as the
 * fallback: this screen is read at the door, by people whose event is not in French.
 * Only the server's own failure reason is passed through verbatim.
 */

/** What the banner says when the badge is not issued, and what we offer to do about it. */
type Blocker = {
  title: string;
  detail: string;
  /** The move that unblocks it — absent when there is none. */
  action?: { label: string; to: string };
};

export function Badge() {
  const t = useT();
  const navigate = useNavigate();
  const event = useEvent();
  useAccessLog("badgeAccess");
  const locale = useLocale();
  const { value } = useSettings();
  const { name: pageName } = useSitePages();

  const { me, recorded, settled, unavailable } = useParticipation();

  /**
   * ═══ THE BALANCE DECIDES, SO WE WAIT FOR IT ═══
   *
   * ⚠️ THE CARD IS NOT PAINTED BEFORE WE KNOW. Rendered while the question is in
   * flight, it would appear and then close again under the participant's eyes — worse
   * than a wait: they had their badge, and it was taken away.
   *
   * ⚠️ ONLY ASKED WHEN THERE IS A REGISTRATION. With no recorded answer there is no
   * order to price, and this screen already has its conclusion.
   *
   * ⚠️ IT DOES NOT DEPEND ON THE TICKETING STEP, for the same reason as the
   * confirmation's payment block: a debt can come from the back office, from a
   * registration made under V1, from a step removed since. The hook keeps quiet by
   * itself when there is nothing to pay, which is the case on almost every event.
   */
  const registered = recorded?.responded === true;
  const {
    resume,
    loading: balancePending,
    failure: balanceUnknown,
  } = useResume(null, registered);

  const state: BadgeState = badgeState({
    ...(recorded ? { responded: recorded.responded } : {}),
    ...(recorded?.answer ? { answer: recorded.answer } : {}),
    /* ⚠️ `undefined` UNTIL WE KNOW — including on failure. The rule lives in
       `badgeState`: we do not withhold the badge of somebody in good standing because a
       request fell over. The real check happens at the door. */
    ...(balanceUnknown || balancePending || !resume
      ? {}
      : { unpaid: resume.toPay }),
  });
  const ready = state === "ready";

  /**
   * THE COMPANY AND THE JOB TITLE, as the printed badge carries them: those of the
   * ATTACHMENT kept for this event (`UserEvents.employeeId`), not of the first company
   * that comes up. Only asked for when the card displays them.
   */
  const companyShown = useFlagOr("FO_MEETING_V2_BADGE_COMPANY");
  const { attached: attachment } = useCompany(companyShown && ready);

  /**
   * THE COMPANIONS. Asked for once the badge is issued: before that the card is not
   * displayed, and the list would have nothing to dress.
   */
  /* Elle ne sert plus qu'aux CARTES, qui n'existent que si le badge est délivré : rien
     à demander tant qu'il ne l'est pas. */
  const { companions: companions } = useCompanions(ready);

  const qrShown = useFlagOr("FO_MEETING_V2_BADGE_QR");
  const idShown = useFlagOr("FO_MEETING_V2_BADGE_ID");
  const pdfShown = useFlagOr("FO_MEETING_V2_BADGE_PDF");
  /**
   * « Envoyer le badge » d'un accompagnant PAR EMAIL (action 4), le bouton que la V1
   * pose sur sa confirmation (`FO_MEETING_CONFIRMATION_SHOW_SEND_BADGE`). Ici, sur la
   * carte de l'accompagnant, à côté de son téléchargement. L'état par personne dit ce
   * qui s'est passé : parti, ou rien de réglé pour cette action.
   */
  const sendShown = useFlagOr("FO_MEETING_V2_BADGE_SEND_SHOW");
  const sendLabel = useText(
    "FO_MEETING_V2_BADGE_SEND_LABEL",
    fallbackOf("FO_MEETING_V2_BADGE_SEND_LABEL"),
  );
  const [mailed, setMailed] = useState<
    Record<number, "sending" | "sent" | "none" | "failed">
  >({});
  const sendBadge = async (companionId: number) => {
    if (!event || mailed[companionId] === "sending") return;
    setMailed((m) => ({ ...m, [companionId]: "sending" }));
    try {
      const { sent } = await sendActionEmail(
        event.domainId,
        event.eventId,
        "badge",
        { userId: companionId },
      );
      setMailed((m) => ({ ...m, [companionId]: sent ? "sent" : "none" }));
    } catch {
      setMailed((m) => ({ ...m, [companionId]: "failed" }));
    }
  };
  const notice = useText("FO_MEETING_V2_BADGE_NOTICE");

  /* Le même nom que le haut de page : le réglage s'il est écrit, sinon celui de
     l'événement. Voir `settings/eventName.ts`. */
  const eventName = useEventName();

  /**
   * ═══ LES DEUX BORNES, L'UNE SOUS L'AUTRE ═══
   *
   * ⚠️ SUR UNE SEULE LIGNE, ELLES SE COUPAIENT N'IMPORTE OÙ. Le badge fait 300 px au
   * plus, et un accompagnant en fait 240 : « 21 septembre 2020 – 22 septembre 2020 »
   * revenait à la ligne au milieu du second mois — « 22 » d'un côté, « septembre 2020 »
   * de l'autre. Une date coupée en deux ne se lit plus comme une date.
   *
   * Le tiret reste en fin de première ligne : deux dates empilées sans lui pourraient
   * se lire comme deux événements.
   */
  const periodes = bornes(
    (event?.days ?? []).map((d) => d.day),
    locale,
  );
  const lines =
    periodes.length > 1 ? [`${periodes[0]} –`, periodes[1]] : periodes;

  const first = firstStep(value, useOffer());
  const go = (path: string) => navigate(withContext(path, event?.token));

  /* AFFICHAGE SEULEMENT : le nom du fichier PDF et le badge imprimé viennent du serveur. */
  const fullName = formatFullName(me?.firstName, me?.lastName);

  const company = attachment
    ? [attachment.name, attachment.jobTitle].filter(Boolean).join(" · ")
    : "";

  /**
   * THE DOWNLOAD — a mutation, for its state and for its error message.
   *
   * ⚠️ THE FAILURE IS TOLD IN THE SERVER'S OWN WORDS — TO THE ORGANISER. "Aucun
   * template badge configuré" is exactly what they need to read in order to fix it.
   * Replaced by a homemade "something went wrong", the reason would be lost at the
   * moment it is useful.
   *
   * ⚠️ NOT TO THE PARTICIPANT (bug #256): the same sentence tells them to configure a
   * template they have no way to reach. They read that the download failed and who
   * can help — the server's reason stays with the organiser's mode.
   */
  const organiser = useMode() === "organiser";
  /** Le nom du fichier d'UNE personne — le sien, ou celui d'un accompagnant. */
  const fileName = (who: null | { name: string; code?: string }) =>
    who
      ? badgeFileName({
          /* ⚠️ UN ACCOMPAGNANT N'A QU'UN NOM D'UN SEUL TENANT — le produit ne sépare pas
             son prénom de son nom sur cette liste. On le donne tel quel plutôt que de le
             couper à l'espace : « Jean-Pierre De La Tour » ne se découpe pas. */
          lastName: who.name,
          ...(who.code ? { code: who.code } : {}),
          eventName,
        })
      : badgeFileName({
          ...(me?.lastName ? { lastName: me.lastName } : {}),
          ...(me?.firstName ? { firstName: me.firstName } : {}),
          ...(recorded?.code ? { code: recorded.code } : {}),
          eventName,
        });

  /**
   * ═══ UN BADGE, OU TOUS DANS UN SEUL DOCUMENT ═══
   *
   * ⚠️ UNE SEULE MUTATION POUR LES DEUX, et c'est ce qui garde l'écran honnête : elle
   * porte l'état de chargement ET l'erreur du serveur. Deux mutations auraient donné
   * deux messages d'échec possibles au même endroit, et un bouton qui tourne pendant
   * qu'un autre a déjà échoué.
   *
   * `cible` : `null` pour soi, l'accompagnant pour le sien, `"tous"` pour le document
   * qui les réunit.
   */
  const pdf = useMutation({
    mutationFn: (
      target: null | "tous" | { id: number; name: string; code?: string },
    ) => {
      const ou = { domainId: event!.domainId, eventId: event!.eventId };
      if (target === "tous")
        return downloadBadge(
          { ...ou, userId: me!.id },
          /* Le document de tout le monde porte le nom du porteur : c'est SON inscription
             qui les réunit, et le fichier atterrit dans SES téléchargements. */
          fileName(null),
          companions.map((c) => c.id),
        );
      if (target)
        return downloadBadge({ ...ou, userId: target.id }, fileName(target));
      return downloadBadge({ ...ou, userId: me!.id }, fileName(null));
    },
    // Sur la fiche du porteur, qui télécharge : le détail nomme le badge.
    onSuccess: (_blob, target) =>
      logAction(event, "badgeDownload", {
        label:
          target === "tous"
            ? `Tous les badges (${companions.length + 1})`
            : target
              ? `Badge de ${target.name}`
              : "Badge",
      }),
  });
  /** Quelle cible tourne — pour n'animer QUE le bouton sur lequel on a appuyé. */
  const enCours = pdf.isPending ? pdf.variables : undefined;

  /** The page's name as the organiser wrote it — the heading follows from it. */
  const page = pageOf("badge");
  const title = page ? pageName(page) : t("MY_BADGE", "Mon badge");

  /**
   * ⚠️ THE PROTOTYPE'S SECOND SENTENCE IS NOT CARRIED OVER. It promises "it stays
   * readable without a connection"; this page reads its registration from the server and
   * draws its QR on arrival — offline it shows nothing at all. Promising otherwise would
   * send somebody to a door with no network and no badge. The day the badge is cached,
   * the sentence comes back with it.
   */
  const subtitle = useText(
    "FO_MEETING_V2_BADGE_SUBTITLE",
    fallbackOf("FO_MEETING_V2_BADGE_SUBTITLE"),
  );
  const html = useText("FO_MEETING_V2_BADGE_HTML");

  const BLOCKERS: Record<Exclude<BadgeState, "ready">, Blocker> = {
    "no-answer": {
      title: t("BADGE_AWAITS_YOU", "Votre badge vous attend"),
      detail: t(
        "BADGE_AFTER_REGISTRATION",
        "Il s'affichera ici dès que votre inscription sera enregistrée.",
      ),
      ...(first
        ? { action: { label: t("REGISTER_ME", "M'inscrire"), to: first.path } }
        : {}),
    },
    unpaid: {
      title: t("BADGE_AWAITS_YOU", "Votre badge vous attend"),
      detail: t(
        "BADGE_AFTER_PAYMENT",
        "Il reste un solde à régler pour cette inscription. Une fois le paiement passé, votre badge s'affiche ici.",
      ),
      action: {
        label: t("PAY_MY_REGISTRATION", "Régler mon inscription"),
        to: CONFIRMATION,
      },
    },
    declined: {
      title: t("YOU_DECLINED", "Vous avez indiqué ne pas participer"),
      detail: t(
        "NO_BADGE_FOR_DECLINED",
        "Il n'y a donc pas de badge à présenter. Vous pouvez revenir sur votre réponse tant que les inscriptions sont ouvertes.",
      ),
      ...(first
        ? {
            action: {
              label: t("CHANGE_MY_ANSWER", "Modifier ma réponse"),
              to: first.path,
            },
          }
        : {}),
    },
    represented: {
      title: t("SOMEBODY_COMES_INSTEAD", "Quelqu'un vient à votre place"),
      detail: t(
        "BADGE_IN_STAND_IN_NAME",
        "Le badge sera établi au nom de la personne que vous avez désignée : c'est elle qui se présentera à l'entrée.",
      ),
      ...(first
        ? {
            action: {
              label: t("CHANGE_MY_ANSWER", "Modifier ma réponse"),
              to: first.path,
            },
          }
        : {}),
    },
    /* Tâche #225 : en ligne, pas de porte, donc ni carte, ni QR, ni téléchargement. La
       sortie est la même que pour un refus : changer sa réponse pour venir sur place. */
    remote: {
      title: t("YOU_ATTEND_REMOTELY", "Vous participez à distance"),
      detail: t(
        "NO_BADGE_FOR_REMOTE",
        "Vous suivez l'événement en ligne : il n'y a pas de badge à présenter. Pour venir sur place, modifiez votre réponse tant que les inscriptions sont ouvertes.",
      ),
      ...(first
        ? {
            action: {
              label: t("CHANGE_MY_ANSWER", "Modifier ma réponse"),
              to: first.path,
            },
          }
        : {}),
    },
  };
  const blocker = ready ? undefined : BLOCKERS[state];

  return (
    <section className="page">
      <div className="ttl" data-cfg="badgeTitle">
        {/* Tâche #557 : le titre se modifie sur place comme le sous-titre. C'est le NOM
            de la page : même clé que sa ligne de « Pages du site », donc le menu et la
            tuile suivent. */}
        {page ? (
          <InPlace
            settingKey={page.nameKey ?? labelKey(page)}
            label="Nom de la page"
            text={title}
            as="h2"
          />
        ) : (
          <h2>{title}</h2>
        )}
        <InPlace settingKey="FO_MEETING_V2_BADGE_SUBTITLE" text={subtitle} as="p" />
      </div>

      {/* Le bloc libre de l'organisateur, sous le titre : son HTML, assaini comme
          partout (`ui/Prose`). Vide, rien ne s'affiche. */}
      {html.trim() !== "" && (
        <Prose html={html} className="se-prose" data-cfg="badgeTitle" />
      )}

      {/**
       * ⚠️ NOTHING IS CLAIMED WHILE THE ANSWERS ARE IN FLIGHT. Neither the card nor the
       * banner that refuses it: both would be a half-second lie, and the one announcing
       * "no badge" to a registered participant is the worse of the two.
       */}
      {(!settled || balancePending) && (
        <div className="confcols">
          <span className="spin" aria-hidden="true" />
          <span className="a11y" role="status">
            {t("LOADING_YOUR_BADGE", "Chargement de votre badge")}
          </span>
        </div>
      )}

      {/* ⚠️ IDENTITÉ ILLISIBLE (échec non-401) : ni la carte ni le bloqueur « inscrivez-vous »,
          qui mentirait à un inscrit dont on n'a pas pu relire la session. On le dit, comme le
          solde inconnu ne retient pas le badge de quelqu'un en règle. */}
      {settled && unavailable && (
        <div className="etat">
          <span className="etat__i" aria-hidden="true">
            <Icon name="verrou" />
          </span>
          <span>
            <b>{t("BADGE_NOT_LOADED", "Votre badge n'a pas pu être chargé")}</b>
            <small>
              {t(
                "SESSION_UNREADABLE",
                "Votre session n'a pas pu être relue. Rechargez la page ; si cela persiste, prévenez l'organisateur.",
              )}
            </small>
          </span>
        </div>
      )}

      {settled && !balancePending && !unavailable && blocker && (
        <>
          <div className="etat">
            <span className="etat__i" aria-hidden="true">
              <Icon name="verrou" />
            </span>
            <span>
              <b>{blocker.title}</b>
              <small>{blocker.detail}</small>
            </span>
          </div>

          {blocker.action && (
            <div className="actions">
              <Button type="button" onClick={() => go(blocker.action!.to)}>
                {blocker.action.label}
              </Button>
            </div>
          )}
        </>
      )}

      {settled && !balancePending && ready && (
        <>
          {/* Yours FIRST, then the companions', in the same grid: a companion is a badge
              holder in their own right, just as you are. */}
          <div className="baccl">
            <div className="badge" data-cfg="badgeCard">
              <div className="badge__t">
                <b>{eventName}</b>
                {lines.length > 0 && (
                  <span className="badge__d">
                    {lines.map((line) => (
                      <span key={line}>{line}</span>
                    ))}
                  </span>
                )}
              </div>
              <div className="badge__b">
                <span className="badge__role">
                  {t("CARD_HOLDER", "Titulaire")}
                </span>
                <span className="badge__n">
                  {fullName || t("YOUR_BADGE", "Votre badge")}
                </span>
                {companyShown && company && (
                  <span className="badge__c">{company}</span>
                )}
                {qrShown && recorded?.code && (
                  <QrCode
                    value={recorded.code}
                    label={t("QR_OF_BADGE_OF", "QR code du badge de $1", [
                      fullName || t("YOUR_REGISTRATION", "votre inscription"),
                    ])}
                  />
                )}
                {/* ⚠️ THE IDENTIFIER IS THE PERSON'S OWN, not an editable string: it is
                    what gets read out at the desk, and one word shared by everybody
                    would be no use there. See the catalog's note. */}
                {idShown && recorded?.code && (
                  <span className="badge__id">{recorded.code}</span>
                )}
                {notice.trim() !== "" && (
                  <span className="badge__c">{notice}</span>
                )}
              </div>

              {/* ⚠️ LE GESTE VIT SUR LA CARTE, TOUJOURS. Avec trois accompagnants, trois
                  boutons « Télécharger » alignés sous l'écran ne diraient plus lequel
                  appartient à qui ; et tout seul, on cherche son badge là où il est
                  dessiné, pas dans une rangée d'actions au bas de la page. */}
              {pdfShown && (
                <button
                  type="button"
                  className="badge__dl"
                  disabled={pdf.isPending}
                  aria-label={t("DOWNLOAD_MY_BADGE", "Télécharger mon badge")}
                  onClick={() => pdf.mutate(null)}
                >
                  <Icon name="fichier" />
                  {enCours === null
                    ? t("DOWNLOADING", "Téléchargement")
                    : t("DOWNLOAD", "Télécharger")}
                </button>
              )}
            </div>

            {companions.map((companion) => (
              <div className="badge badge--acc" key={companion.id}>
                <div className="badge__t">
                  <b>{eventName}</b>
                  {lines.length > 0 && (
                    <span className="badge__d">
                      {lines.map((line) => (
                        <span key={line}>{line}</span>
                      ))}
                    </span>
                  )}
                </div>
                <div className="badge__b">
                  <span className="badge__role">
                    {t("COMPANION", "Accompagnant")}
                  </span>
                  <span className="badge__n">{companion.label}</span>
                  {/* ⚠️ THEIR code, never the holder's — and nothing at all if they have
                      none yet: a card without a QR can still be presented, whereas a QR
                      matching nobody holds up the queue. */}
                  {qrShown && companion.code && (
                    <QrCode
                      value={companion.code}
                      label={t("QR_OF_BADGE_OF", "QR code du badge de $1", [
                        companion.label,
                      ])}
                    />
                  )}
                  {idShown && companion.code && (
                    <span className="badge__id">{companion.code}</span>
                  )}
                  {notice.trim() !== "" && (
                    <span className="badge__c">{notice}</span>
                  )}
                </div>

                {pdfShown && (
                  <button
                    type="button"
                    className="badge__dl"
                    disabled={pdf.isPending}
                    aria-label={t(
                      "DOWNLOAD_BADGE_OF",
                      "Télécharger le badge de $1",
                      [companion.label],
                    )}
                    onClick={() =>
                      pdf.mutate({
                        id: companion.id,
                        name: companion.name,
                        ...(companion.code ? { code: companion.code } : {}),
                      })
                    }
                  >
                    <Icon name="fichier" />
                    {typeof enCours === "object" &&
                    enCours !== null &&
                    enCours.id === companion.id
                      ? t("DOWNLOADING", "Téléchargement")
                      : t("DOWNLOAD", "Télécharger")}
                  </button>
                )}
                {sendShown && (
                  <button
                    type="button"
                    className="badge__dl"
                    disabled={mailed[companion.id] === "sending"}
                    aria-label={`${sendLabel} : ${companion.label}`}
                    onClick={() => void sendBadge(companion.id)}
                  >
                    <Icon name="mail" />
                    {mailed[companion.id] === "sent"
                      ? t("BADGE_EMAIL_SENT", "Badge envoyé")
                      : mailed[companion.id] === "none"
                        ? t("BADGE_EMAIL_UNAVAILABLE", "Envoi non configuré")
                        : mailed[companion.id] === "failed"
                          ? t("BADGE_EMAIL_FAILED", "Envoi impossible")
                          : sendLabel}
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* ⚠️ THE FAILURE IS TOLD ABOVE THE BUTTON, with the server's reason for the
              organiser. A download that never leaves shows NOTHING on screen: without
              this line, people click again. */}
          {/**
           * ⚠️ LE DOCUMENT COMMUN PEUT ÊTRE REFUSÉ, ET POUR UNE BONNE RAISON : le
           * serveur résout le modèle de badge pour CHAQUE porteur et refuse d'en choisir
           * un au hasard quand leurs langues ou leurs typologies en désignent des
           * différents. Mesuré sur l'événement de test : « Ces porteurs n'ont pas le
           * même template badge configuré (7, 8) ».
           *
           * On répète sa phrase à l'organisateur — c'est la règle de cet écran, et il
           * est le seul à pouvoir y remédier — mais on ne laisse personne dans
           * l'impasse : chaque carte porte son propre bouton, et c'est ce qu'on dit.
           * Le participant, lui, lit l'échec sans la raison : voir `organiser`.
           */}
          {pdf.isError && (
            <p className="hint" role="alert">
              {pdf.variables === "tous"
                ? organiser
                  ? t(
                      "BADGES_NOT_MERGED",
                      "Les badges n'ont pas pu être réunis en un seul document : $1 Vous pouvez les télécharger un par un, avec le bouton de chaque carte.",
                      [(pdf.error as Error).message],
                    )
                  : t(
                      "BADGES_NOT_MERGED_RETRY",
                      "Les badges n'ont pas pu être réunis en un seul document. Vous pouvez les télécharger un par un, avec le bouton de chaque carte.",
                    )
                : organiser
                  ? t(
                      "BADGE_NOT_DOWNLOADED",
                      "Le badge n'a pas pu être téléchargé : $1",
                      [(pdf.error as Error).message],
                    )
                  : t(
                      "BADGE_NOT_DOWNLOADED_RETRY",
                      "Le badge n'a pas pu être téléchargé. Réessayez dans un instant, et prévenez l'organisateur si cela persiste.",
                    )}
            </p>
          )}

          <div className="actions" data-cfg="badgeActions">
            <Button type="button" variant="ghost" onClick={() => go("/")}>
              {t("BACK_TO_HOME", "Retour à l'accueil")}
            </Button>
            {/**
             * ═══ LA RANGÉE N'OFFRE QUE LE LOT ═══
             *
             * ⚠️ ET SEULEMENT S'IL Y A DU MONDE. Le téléchargement d'UN badge vit sur sa
             * carte, où il se présente comme le geste principal : un second bouton ici
             * serait deux chemins vers le même fichier — une hésitation, pas un choix.
             *
             * ⚠️ IL LES RÉUNIT EN UN DOCUMENT. La route sait imprimer par lot ; trois
             * téléchargements séparés, le navigateur en bloque deux, et il resterait
             * trois fichiers à imprimer un par un alors qu'on veut une feuille à
             * découper.
             *
             * ⚠️ LE VERBE, ET PAS LE COMPTE. « Tous les badges (3) » nommait un ensemble
             * sans dire ce qu'on en fait — à côté de trois boutons « Télécharger », il se
             * lisait comme un filtre ou un lien vers une liste. Le nombre, lui, est déjà
             * à l'écran : les cartes sont là.
             */}
            {pdfShown && companions.length > 0 && (
              <Button
                type="button"
                loading={enCours === "tous"}
                disabled={pdf.isPending}
                onClick={() => pdf.mutate("tous")}
              >
                {t("DOWNLOAD_ALL_BADGES", "Télécharger tous les badges")}
              </Button>
            )}
          </div>
        </>
      )}
    </section>
  );
}
